"use client";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/buttons/Button";
import { OverlaySection } from "@/components/tutorial/OverlaySection";
import { PADDING_TARGET_ELEMENT } from "@/constants/tutorial";
import { useTutorial } from "@/lib/zustand/useTutorial/useTutorial";
import { UserNoDates } from "@/types/types";
import { createActivationKeyDownHandler } from "@/utils/a11y/createActivationKeyDownHandler";

export function TutorialOverlay({
  user,
  simplyUser,
}: {
  user: UserNoDates | null;
  simplyUser: boolean;
}) {
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const {
    styles,
    attributes,
    state: popperState,
    forceUpdate,
  } = usePopper(referenceElement, popperElement, {
    placement: "auto",

    modifiers: [
      { name: "offset", options: { offset: [0, 12] } },
      { name: "arrow", options: { element: arrowElement } },
      {
        name: "flip",
        options: {
          allowedAutoPlacements: ["top", "bottom"],
          fallbackPlacements: ["left", "right"],
        },
      },
    ],
  });

  const endWelcomeModalAndTutorial = useTutorial(
    (state) => state.endWelcomeModalAndTutorial
  );
  const isActive = useTutorial((state) => state.isActive);
  const currentStepIndex = useTutorial((state) => state.currentStepIndex);
  const tutorialSteps = useTutorial((state) => state.tutorialSteps);
  const nextStep = useTutorial((state) => state.nextStep);
  const targetElement = useTutorial((state) => state.targetElement);

  // If the target element is resized, force a re-render of the popper tooltip. This will cause the component to re-render which will also make the overlay calculations re-run.
  useEffect(() => {
    if (targetElement === null) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (forceUpdate !== null) {
        forceUpdate();
      }
    });

    resizeObserver.observe(targetElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [targetElement, forceUpdate]);

  const handleClose = async () => {
    // Should never happen, just for TypeScript
    if (user === null) {
      return;
    }

    await endWelcomeModalAndTutorial(user, simplyUser);
  };

  // Close the tutorial when the user presses the escape key. This also serves as a way to prevent the user from closing the modal when the tutorial is active.
  useEffect(() => {
    const handleEscapeKey = async (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Esc") {
        e.stopPropagation();
        e.preventDefault();
        await handleClose();
      }
    };

    if (isActive) {
      window.addEventListener("keydown", handleEscapeKey);
      return () => {
        window.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [isActive, endWelcomeModalAndTutorial, user, simplyUser]);

  // If the user scrolls, force a re-render of the popper tooltip. This will cause the component to re-render which will also make the overlay calculations re-run. Popper has scroll event listeners already, but they do not work with react portals (e.g. our Modal component), that's why we need to add our own that does capture: true.
  useEffect(() => {
    const handleScroll = () => {
      if (forceUpdate !== null) {
        forceUpdate();
      }
    };

    if (isActive) {
      window.addEventListener("scroll", handleScroll, { capture: true });

      return () =>
        window.removeEventListener("scroll", handleScroll, { capture: true });
    }
  }, [forceUpdate]);

  // Don't render anything when closed
  if (!isActive) {
    return null;
  }

  // The tutorial should never be on if there is no logged in user
  if (user === null) {
    return null;
  }

  const noTargetElement = targetElement === null;

  const currentStep = tutorialSteps[currentStepIndex];

  const isLastStep = currentStepIndex >= tutorialSteps.length - 1;

  const handleNextStep = async () => {
    if (isLastStep) {
      await handleClose();
    } else {
      await nextStep();
    }
  };

  const getPopperPlacement = () => {
    if (popperState === null) {
      return "";
    }

    if (popperState.placement.includes("top")) {
      return "bottom-0";
    }
    if (popperState.placement.includes("left")) {
      return "right-0";
    }
    return "";
  };

  const targetRect =
    targetElement === null ? null : targetElement.getBoundingClientRect();
  const holeStyle =
    targetRect === null ? undefined : (
      {
        top: targetRect.top - PADDING_TARGET_ELEMENT,
        left: targetRect.left - PADDING_TARGET_ELEMENT,
        width: targetRect.width + PADDING_TARGET_ELEMENT * 2,
        height: targetRect.height + PADDING_TARGET_ELEMENT * 2,
      }
    );

  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-hidden">
      <>
        {/* If no target element, show a full screen overlay */}
        {noTargetElement ?
          <div className="absolute inset-0 z-50 bg-gray-900 bg-opacity-10" />
        : <>
            {/* If there is a target element, show the overlay sections, the hole and the tooltip */}
            <OverlaySection position="top" />
            <OverlaySection position="left" />
            <OverlaySection position="right" />
            <OverlaySection position="bottom" />

            {/* Actual hole; when the step advances on a hole click it is keyboard-operable too */}
            {currentStep.nextOnHoleClick ?
              <div
                role="button"
                tabIndex={0}
                aria-label="Continue to the next tutorial step"
                className="absolute cursor-pointer rounded-3xl transition-opacity duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                onClick={handleNextStep}
                onKeyDown={createActivationKeyDownHandler(handleNextStep)}
                ref={setReferenceElement}
                style={holeStyle}
              />
            : <div
                className="absolute cursor-pointer rounded-3xl transition-opacity duration-100"
                ref={setReferenceElement}
                style={holeStyle}
              />
            }

            {/* Tooltip */}
            <div
              ref={setPopperElement}
              style={styles.popper}
              {...attributes.popper}
              className="relative"
            >
              {/* Arrow */}
              <div
                ref={setArrowElement}
                style={styles.arrow}
                className={twMerge(
                  "invisible absolute h-5 w-5 bg-inherit before:visible before:absolute before:h-5 before:w-5 before:rotate-45 before:bg-white",
                  // Should not be needed, a bug in react-popper
                  getPopperPlacement()
                )}
              />

              {/* Content */}
              <div className="m-2 max-w-sm rounded-lg bg-white p-4">
                <div className="mb-4">{currentStep.content}</div>
                <div className="flex">
                  {currentStep.showNextButton && (
                    <Button onClick={handleNextStep} className="w-full flex-1">
                      {isLastStep ? "Finish" : "Next"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </>
        }

        {/* Close button */}
        <button
          className="absolute right-5 top-5 z-50 rounded-lg bg-white p-3 shadow-lg sm:bottom-5 sm:top-auto"
          onClick={handleClose}
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </>
    </div>,
    document.body
  );
}
